"use client";

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocale } from "@/i18n";
import { useAuth } from "@/hooks";
import {
  buildVerificationHrefWithNext,
  getSafeAuthorizeContinuation,
  isSafeAppPath,
} from "@/helpers";
import { APP_ORIGIN } from "@/constants";

interface UseRequireGuestOptions {
  redirectTo?: string;
  autoRedirect?: boolean;
  redirectDelay?: number;
  shouldRedirect?: (user: any) => boolean;
  checkEmailVerification?: boolean;
  unverifiedEmailRedirect?: string;
}

interface UseRequireGuestReturn {
  isGuest: boolean;
  isLoading: boolean;
  user: any;
}

const useRequireGuest = ({
  redirectTo,
}: UseRequireGuestOptions): UseRequireGuestReturn => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useLocale();
  const isGuest = !isAuthenticated;

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    if (!user.onboardingCompleted && user.role !== "demo") {
      navigate(buildVerificationHrefWithNext(locale, location.search), {
        replace: true,
      });
      return;
    }

    // An already signed-in user can arrive here from the OIDC provider's
    // authorize continuation. Return to that same-origin endpoint so it can
    // finish the pending transaction; never substitute the product APP_ORIGIN.
    const authorizeContinuation = getSafeAuthorizeContinuation(location.search);
    if (authorizeContinuation) {
      window.location.assign(authorizeContinuation);
      return;
    }

    if (redirectTo && isSafeAppPath(redirectTo) && redirectTo !== "/") {
      navigate(redirectTo);
      return;
    }

    const base = APP_ORIGIN || "http://localhost:3001";
    window.location.assign(`${base.replace(/\/+$/, "")}/${locale}`);
  }, [isLoading, isAuthenticated, user, redirectTo, locale, location.search, navigate]);

  return {
    isGuest,
    isLoading,
    user,
  };
};

export { useRequireGuest };
